import type { Module } from 'vuex';
import { BlockchainAddress } from '@/types/BlockchainAddress';
import {RootState} from "@/store";

export interface BlockchainAddressesState {
  blockchainAddresses: BlockchainAddress[];
}

const blockchainAddressesModule: Module<BlockchainAddressesState, RootState> = {
  namespaced: true,

  state: (): BlockchainAddressesState => ({
    blockchainAddresses: [],
  }),

  getters: {
    getById: (state) => (idBlockchainAddress: number): BlockchainAddress | undefined => {
      return state.blockchainAddresses.find(blockchainAddress => blockchainAddress.idBlockchainAddress === idBlockchainAddress);
    },
    getByBlockchain: (state) => (idBlockchain: number): BlockchainAddress[] => {
      return state.blockchainAddresses.filter(blockchainAddress => blockchainAddress.idBlockchain === idBlockchain);
    },
  },

  mutations: {
    setBlockchainAddresses(
      state,
      payload: { blockchainAddresses: Record<number, any> }
    ) {
      state.blockchainAddresses = Object
        .values(payload.blockchainAddresses)
        .map(BlockchainAddress.fromRaw);
    },

    setBlockchainAddressCreate(
      state,
      payload: {
        idBlockchainAddress: number;
        idBlockchain: number;
        formatName: string;
        prefix: string;
        regexPattern: string;
        minLength: number;
        maxLength: number;
        checksumAlgorithm: string;
        hasMemoTag: boolean;
        description: string | null;
        locked: boolean;
      }
    ) {
      const raw = {
        idBlockchainAddress: payload.idBlockchainAddress,
        idBlockchain: payload.idBlockchain,
        formatName: payload.formatName,
        prefix: payload.prefix,
        regexPattern: payload.regexPattern,
        minLength: payload.minLength,
        maxLength: payload.maxLength,
        checksumAlgorithm: payload.checksumAlgorithm,
        hasMemoTag: payload.hasMemoTag,
        description: payload.description,
        locked: payload.locked,
      };
      state.blockchainAddresses.push(BlockchainAddress.fromRaw(raw));
    },

    setBlockchainAddressUpdate(
      state,
      payload: {
        idBlockchainAddress: number;
        idBlockchain?: number;
        formatName?: string;
        prefix?: string;
        regexPattern?: string;
        minLength?: number;
        maxLength?: number;
        checksumAlgorithm?: string;
        hasMemoTag?: boolean;
        description?: string | null;
      }
    ) {
      const blockchainAddress = state.blockchainAddresses.find(blockchainAddress => blockchainAddress.idBlockchainAddress === payload.idBlockchainAddress);
      if (!blockchainAddress) return;
      if (payload.idBlockchain !== undefined) blockchainAddress.idBlockchain = payload.idBlockchain;
      if (payload.formatName !== undefined) blockchainAddress.formatName = payload.formatName;
      if (payload.prefix !== undefined) blockchainAddress.prefix = payload.prefix;
      if (payload.regexPattern !== undefined) blockchainAddress.regexPattern = payload.regexPattern;
      if (payload.minLength !== undefined) blockchainAddress.minLength = payload.minLength;
      if (payload.maxLength !== undefined) blockchainAddress.maxLength = payload.maxLength;
      if (payload.checksumAlgorithm !== undefined) blockchainAddress.checksumAlgorithm = payload.checksumAlgorithm;
      if (payload.hasMemoTag !== undefined) blockchainAddress.hasMemoTag = payload.hasMemoTag;
      if (payload.description !== undefined) blockchainAddress.description = payload.description;
    },

    setBlockchainAddressDelete(
      state,
      payload: { idBlockchainAddress: number }
    ) {
      state.blockchainAddresses = state.blockchainAddresses.filter(
        blockchainAddress => blockchainAddress.idBlockchainAddress !== payload.idBlockchainAddress
      );
    },

    setBlockchainAddressLock(
      state,
      payload: { idBlockchainAddress: number }
    ) {
      const blockchainAddress = state.blockchainAddresses.find(blockchainAddress => blockchainAddress.idBlockchainAddress === payload.idBlockchainAddress);
      if (blockchainAddress) blockchainAddress.locked = true;
    },

    setBlockchainAddressUnlock(
      state,
      payload: { idBlockchainAddress: number }
    ) {
      const blockchainAddress = state.blockchainAddresses.find(blockchainAddress => blockchainAddress.idBlockchainAddress === payload.idBlockchainAddress);
      if (blockchainAddress) blockchainAddress.locked = false;
    },
  },

  actions: {
    wsSetBlockchainAddresses({ commit }, payload) {
      commit('setBlockchainAddresses', payload);
    },
    wsBlockchainAddressCreate({ commit }, payload) {
      commit('setBlockchainAddressCreate', payload);
    },
    wsBlockchainAddressUpdate({ commit }, payload) {
      commit('setBlockchainAddressUpdate', payload);
    },
    wsBlockchainAddressDelete({ commit }, payload) {
      commit('setBlockchainAddressDelete', payload);
    },
    wsBlockchainAddressLock({ commit }, payload) {
      commit('setBlockchainAddressLock', payload);
    },
    wsBlockchainAddressUnlock({ commit }, payload) {
      commit('setBlockchainAddressUnlock', payload);
    },
  },
};

export default blockchainAddressesModule;
